import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Repositories } from '@core/db';
import type { CaseDocument, DocumentType } from '@core/domain/model';
import {
  canShareFiles,
  isWordDocument,
  pageCountOf,
  shareFiles,
  type FileStore,
} from '@core/files';
import { kindOf, size, typeLabel } from './formatter';
import { titleOf } from './imports';
import type { DocumentKind } from './types';
import { telemetry } from '@core/telemetry';

/** Чем показывать содержимое: страницами, картинкой или ничем, кроме «Поделиться». */
export type ViewerContent =
  | { kind: 'pdf'; content: Blob }
  | { kind: 'image'; url: string }
  /**
   * Документ Word показывается текстом прямо здесь ([D-60](docs/DECISIONS.md)):
   * до этого он открывался только уходом в другое приложение, а в суде на это
   * нет ни времени, ни рук.
   */
  | { kind: 'word'; content: Blob }
  /**
   * Всё, для чего просмотрщика нет: таблицы, презентации, архивы, старый `.doc`.
   * На телефоне уже есть приложение, которое умеет это лучше. Сюда же попадает файл,
   * который не удалось прочитать, — `broken` различает эти случаи, потому что юристу
   * нужны разные слова.
   */
  | { kind: 'external'; broken: boolean };

export interface ViewerState {
  isLoading: boolean;
  notFound: boolean;
  title: string;
  /** «Иванов — раздел имущества · PDF · 9 стр. · 1,2 МБ · 15.08.2026» */
  subtitle: string;
  typeLabel: string;
  isFavorite: boolean;
  fileKind: DocumentKind;
  content: ViewerContent | null;
  errorMessage: string | null;
  /** Документ удалён и ждёт «Отменить» в списке: просмотр закрывается. */
  isDeleted: boolean;
  /** Клиент, к которому привязан документ, — «Структура» §7.2. */
  clientName: string | null;
  clientId: string | null;
  /** Все клиенты: из них выбирают, чей это документ. */
  clients: { id: string; name: string }[];
}

/**
 * Данные просмотра документа.
 *
 * Файл читается из хранилища один раз и держится здесь: страницы рисует pdf.js,
 * «Поделиться» отдаёт те же байты, и второе чтение с диска ради этого не нужно.
 */
export function useViewer(documentId: string, db: Repositories, files: FileStore) {
  const [item, setItem] = useState<CaseDocument | null>(null);
  const [caseTitle, setCaseTitle] = useState('');
  const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
  const [content, setContent] = useState<ViewerContent | null>(null);
  const [file, setFile] = useState<Blob | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isDeleted, setDeleted] = useState(false);
  const [errorMessage, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const refresh = useCallback(() => setVersion((value) => value + 1), []);

  /*
   * Открытие документа. Отдельным эффектом от загрузки: та перезапускается после
   * каждой правки — переименовал, поставил тип, — и одно открытие считалось бы
   * за пять.
   */
  useEffect(() => {
    telemetry.record('documentOpened');
  }, [documentId]);

  useEffect(() => {
    let dropped = false;
    let objectUrl: string | null = null;

    void (async () => {
      const found = await db.documents.find(documentId);
      if (dropped) return;

      if (!found) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const owner = found.caseId ? await db.cases.find(found.caseId) : null;
      const known = await db.clients.list();
      const stored = found.caseId ? await files.open(found.caseId, found.fileName) : null;
      if (dropped) return;

      setItem(found);
      setCaseTitle(owner?.title ?? '');
      setClients(known.map((client) => ({ id: client.id, name: client.fullName })));
      setFile(stored?.ok ? stored.value : null);

      if (!stored?.ok) {
        setContent({ kind: 'external', broken: true });
      } else if (kindOf(found.mimeType) === 'pdf') {
        setContent({ kind: 'pdf', content: stored.value });
      } else if (kindOf(found.mimeType) === 'photo') {
        objectUrl = URL.createObjectURL(stored.value);
        setContent({ kind: 'image', url: objectUrl });
      } else if (isWordDocument(found.mimeType, found.fileName)) {
        setContent({ kind: 'word', content: stored.value });
      } else {
        setContent({ kind: 'external', broken: false });
      }
      setLoading(false);
    })();

    return () => {
      dropped = true;
      // Ссылка на снимок живёт ровно столько, сколько экран: иначе память
      // не отдаётся до перезагрузки приложения.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [db, files, documentId, version]);

  const state: ViewerState = useMemo(() => {
    const pages = item?.pageCount === null || item === null ? null : `${item.pageCount} стр.`;
    const kind = item ? kindOf(item.mimeType) : 'other';
    // «Word» вместо безликого «Файл»: юрист по шапке понимает, что откроется текстом.
    const isWord = item ? isWordDocument(item.mimeType, item.fileName) : false;
    const kindLabel =
      kind === 'pdf' ? 'PDF' : kind === 'photo' ? 'Фото' : isWord ? 'Word' : 'Файл';

    return {
      isLoading,
      notFound,
      title: item?.title ?? '',
      // Дело в шапке — «Структура» §4: видно, из какого дела документ, не возвращаясь.
      subtitle: [
        caseTitle,
        kindLabel,
        pages,
        item ? size(item.sizeBytes) : null,
        item ? new Date(item.addedAt).toLocaleDateString('ru-RU') : null,
      ]
        .filter((part): part is string => Boolean(part))
        .join(' · '),
      typeLabel: item ? typeLabel(item.type) : '',
      isFavorite: item?.isFavorite ?? false,
      fileKind: kind,
      content,
      errorMessage,
      isDeleted,
      clientId: item?.clientId ?? null,
      clientName: clients.find((client) => client.id === item?.clientId)?.name ?? null,
      clients,
    };
  }, [item, caseTitle, clients, content, isLoading, notFound, errorMessage, isDeleted]);

  const actions = useMemo(
    () => ({
      clearError: () => setError(null),

      async toggleFavorite() {
        if (!item) return;
        telemetry.record('documentFavorited');
        await db.documents.setFavorite(item.id, !item.isFavorite);
        refresh();
      },

      async rename(title: string) {
        if (!item || title.trim().length === 0) return;
        await db.documents.rename(item.id, title);
        telemetry.record('documentRenamed');
        refresh();
      },

      /**
       * Чей это документ.
       *
       * Паспорт клиента или доверенность относятся к человеку, а не к делу, — и в его
       * карточке есть раздел «Документы». Пока привязать документ было нечем, раздел
       * не мог заполниться никогда: `clientId` всегда писался пустым.
       */
      async setClient(clientId: string | null) {
        if (!item) return;
        await db.documents.setClient(item.id, clientId);
        refresh();
      },

      async setType(type: DocumentType) {
        if (!item) return;
        telemetry.record('documentTyped');
        await db.documents.setType(item.id, type);
        refresh();
      },

      /**
       * Замена содержимого: название, тип, порядок и избранное остаются прежними.
       *
       * Юрист переснял тот же документ получше — это тот же документ, а не новый:
       * ссылки на него из карточки дела не должны разрываться.
       */
      async replaceContent(next: File) {
        if (!item?.caseId) return;

        const stored = await files.store(item.caseId, item.id, next.name, next);
        if (!stored.ok) {
          setError('Не удалось заменить файл');
          return;
        }

        await db.documents.replaceContent(
          item.id,
          stored.value.fileName,
          next.type || 'application/octet-stream',
          stored.value.sizeBytes,
        );

        // Заменили файл — изменилась и толщина документа: считаем страницы заново.
        const pages = next.type === 'application/pdf' ? await pageCountOf(next) : null;
        if (pages !== null) await db.documents.setPageCount(item.id, pages);

        refresh();
      },

      /** Удаление закрывает просмотр: смотреть больше нечего, «Отменить» ждёт в списке. */
      async remove() {
        if (!item) return;
        await db.documents.remove(item.id);
        setDeleted(true);
      },

      /**
       * «Поделиться» — вызывается прямо из нажатия и берёт уже прочитанные байты:
       * после `await`, не связанного с жестом, Safari не откроет системный лист.
       */
      share(): boolean {
        if (!item || !file) return false;

        const shared = [new File([file], item.fileName, { type: item.mimeType })];
        if (!canShareFiles(shared)) {
          setError('Не удалось открыть «Поделиться»');
          return false;
        }

        // Считается и здесь, и в списке документов: отправить можно с обоих экранов,
        // и счётчик на одном занижал бы цифру ровно на тех, кто работает со второго.
        telemetry.record('documentsShared');
        void shareFiles(shared, titleOf(item.title)).then((outcome) => {
          if (outcome === 'failed' || outcome === 'unsupported') {
            setError('Не удалось открыть «Поделиться»');
          }
        });
        return true;
      },

      refresh,
    }),
    [db, files, item, file, refresh],
  );

  return { state, actions };
}

export type ViewerActions = ReturnType<typeof useViewer>['actions'];
